import { forwardRef, useState, MouseEvent } from 'react'
import { CircularProgress, IconButton, Box, Menu, MenuItem } from '@mui/material'
import dayjs from 'dayjs'

/* Utils */
import { Label, SystemEventName, VisibleLabel } from 'models'
import { getSystemEventValue, getUtcDateAndTime, getLabelActionProps } from '../../../../library/utils'
import { useLibraryDetails } from 'contexts/LibraryDetailsContext'

/* Components */
import { Container, dropdownMenuSx } from './trendsLabelCard.styles'
import { LibraryDetailsHoverTooltip } from 'ui/components/tooltip/LibraryDetailsTooltip'
import { LabelEditIcon, LabelPinIcon, PrivateLabelIcon, PublicLabelIcon } from 'ui/components/svg'
import { OnyxiaTooltip } from 'ui/components/tooltip'

type Ref = HTMLDivElement | null

interface Props {
  width: number
  isCustomLabel: boolean
  label: VisibleLabel
  handlePin: (labelId: string) => void
  handleEdit: (label: Label) => void
  yPosition: number
}

const TrendsLabelCard = forwardRef<Ref, Props>(
  ({ width, isCustomLabel, label, handlePin, handleEdit, yPosition }, ref) => {
    const { setMode, setLabelDrawerOpen, setEditLabel } = useLibraryDetails()
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
    const open = Boolean(anchorEl)
    const description = isCustomLabel ? label.data.description : label.description
    const title = label.data.title || ''
    const { titleColor, icon } = getLabelActionProps(label.action)
    const [pinning, setPinning] = useState(false)

    const onPin = async (labelId: string) => {
      setPinning(true)

      try {
        await handlePin(labelId)
      } catch (e) {
        console.error(e)
      } finally {
        setPinning(false)
      }
    }

    const handleDropdownClick = (e: MouseEvent<HTMLButtonElement>) => {
      setAnchorEl(e.currentTarget)
    }

    const handleDelete = async () => {
      setAnchorEl(null)

      setMode('delete')
      setEditLabel(label as any)
      setLabelDrawerOpen(true)
    }

    const handleClose = () => {
      setAnchorEl(null)
      handleEdit(label)
    }

    return (
      <Container
        ref={ref}
        className="label-card"
        xPosition={label.x}
        yPosition={yPosition}
        containerWidth={width}
        isLastStripe={label.x >= width - 100}
        short={description.length > 25 ? 'false' : isCustomLabel && title.length > 9 ? 'false' : 'true'}
        sx={{
          alignItems: isCustomLabel ? 'center' : 'flex-start',

          '.label-card__header-text': {
            color: titleColor
          }
        }}
      >
        <div className="label-card__content">
          <div className="label-card__header">
            <div className="label-card__header-title-wrapper">
              {icon}
              <p className="label-card__header-text">
                {isCustomLabel ? label.data.title : getSystemEventValue(label.action)}
              </p>
            </div>

            <div className="label-card__actions">
              <LibraryDetailsHoverTooltip title="Unpin Label" placement="bottom-end">
                <IconButton onClick={() => onPin(label.id)} className="action-icon-button">
                  {pinning ? <CircularProgress size={16} color="secondary" /> : <LabelPinIcon />}
                </IconButton>
              </LibraryDetailsHoverTooltip>
              {isCustomLabel && (
                <Box className="dropdown-menu-wrapper">
                  <IconButton
                    onClick={(e) => handleDropdownClick(e)}
                    className="action-icon-button"
                    id="dropdown-button"
                    aria-controls={open ? 'dropdown-menu' : undefined}
                    aria-haspopup="true"
                    aria-expanded={open ? 'true' : undefined}
                  >
                    <LabelEditIcon />
                  </IconButton>

                  <Menu
                    id="dropdown-menu"
                    anchorEl={anchorEl}
                    open={open}
                    onClose={handleClose}
                    MenuListProps={{
                      'aria-labelledby': 'dropdown-button'
                    }}
                    sx={dropdownMenuSx}
                  >
                    <MenuItem onClick={handleClose}>Edit</MenuItem>
                    <MenuItem onClick={handleDelete}>Delete</MenuItem>
                  </Menu>
                </Box>
              )}
            </div>
          </div>
          {description.length > 25 ? (
            <OnyxiaTooltip placement="bottom" title={description} sx={{ width: 'fit-content', maxWidth: '307px' }}>
              <p className="label-card__text">{description}</p>
            </OnyxiaTooltip>
          ) : (
            <p className="label-card__text">{description}</p>
          )}
          <div className="label-card__subtext-wrapper">
            <p className="label-card__subtext">
              {isCustomLabel
                ? dayjs(getUtcDateAndTime(label.data.date || '').labelFormatDate).format('MMM DD, YYYY')
                : dayjs(getUtcDateAndTime(label.inserted_at).labelFormatDate).format('MMM DD, YYYY')}
            </p>

            {label.action !== SystemEventName.CpiCustom ? (
              <PublicLabelIcon />
            ) : label.data.is_public ? (
              <PublicLabelIcon />
            ) : (
              <PrivateLabelIcon />
            )}
          </div>
        </div>
      </Container>
    )
  }
)

export default TrendsLabelCard
